import { useState, useRef, useEffect, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { LuFilter } from "react-icons/lu";
import { IoMdClose } from "react-icons/io";
import { TbFileInvoice, TbCircleCheck, TbLink, TbClipboardCheck, TbClipboardX, TbMailOff, TbPhoneOff, TbMapPinOff } from "react-icons/tb";
import "../Invoices/invoiceFilterBar.scss";
import { useFitToViewport } from "src/hooks/useFitToViewport";
import { sanitizeInput } from "src/helpers/sanitizeInput";
import { getTagStyle } from "src/helpers/randomColor";
import type { Group } from "src/types";

export type RecipientFilters = {
    openInvoices: boolean;
    paidInvoices: boolean;
    questionnaireSent: boolean;
    hasReceivedLink: boolean;
    noQuestionnaireSent: boolean;
    questionnaireNotSubmitted: boolean;
    noEmail: boolean;
    noTel: boolean;
    noAddress: boolean;
    patient: string;
    tel: string;
    address: string;
    email: string;
    group: string;
};

export const DEFAULT_RECIPIENT_FILTERS: RecipientFilters = {
    openInvoices: false,
    paidInvoices: false,
    questionnaireSent: false,
    hasReceivedLink: false,
    noQuestionnaireSent: false,
    questionnaireNotSubmitted: false,
    noEmail: false,
    noTel: false,
    noAddress: false,
    patient: "",
    tel: "",
    address: "",
    email: "",
    group: "",
};

interface Props {
    filters: RecipientFilters;
    onChange: (f: RecipientFilters) => void;
    groups?: Group[];
}

export default function RecipientFilterBar({ filters, onChange, groups }: Props) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    useFitToViewport(panelRef, open);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const activeCount = [
        filters.openInvoices,
        filters.paidInvoices,
        filters.questionnaireSent,
        filters.hasReceivedLink,
        filters.noQuestionnaireSent,
        filters.questionnaireNotSubmitted,
        filters.noEmail,
        filters.noTel,
        filters.noAddress,
        filters.patient !== "",
        filters.tel !== "",
        filters.address !== "",
        filters.email !== "",
        filters.group !== "",
    ].filter(Boolean).length;

    const set = <K extends keyof RecipientFilters>(key: K, val: RecipientFilters[K]) =>
        onChange({ ...filters, [key]: val });

    return (
        <div className="inv-filter" ref={ref}>
            <button
                className={`inv-filter__btn${activeCount > 0 ? " inv-filter__btn--active" : ""}`}
                onClick={() => setOpen((o) => !o)}
            >
                <LuFilter />
                {t("filters.filters")}
                {activeCount > 0 && (
                    <span className="inv-filter__badge">{activeCount}</span>
                )}
            </button>

            {open && (
                <div className="inv-filter__panel" ref={panelRef}>
                    <div className="inv-filter__panel-header">
                        <span>{t("filters.filters")}</span>
                        {activeCount > 0 && (
                            <button
                                className="inv-filter__clear"
                                onClick={() => onChange(DEFAULT_RECIPIENT_FILTERS)}
                            >
                                <IoMdClose /> {t("filters.clearAll")}
                            </button>
                        )}
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("filters.invoices")}</span>
                        <div className="inv-filter__chips">
                            <button
                                className={`inv-filter__chip inv-filter__chip--orange${filters.openInvoices ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("openInvoices", !filters.openInvoices)}
                            >
                                <TbFileInvoice />{t("filters.hasOpen")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--green${filters.paidInvoices ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("paidInvoices", !filters.paidInvoices)}
                            >
                                <TbCircleCheck />{t("filters.hasPaid")}
                            </button>
                            {/* Checks invoicesDone+invoicesOpen-invoicesNotSent > 0 — an invoice
                                delivery signal, unrelated to Einwilligung consent. Previously
                                grouped under "Portal" alongside the consent chips below, which
                                made it look like a "consent link sent but not signed yet" state;
                                it isn't, so it belongs here instead (see PatientFilterBar.tsx,
                                which already dropped this from its own Portal group). */}
                            <button
                                className={`inv-filter__chip${filters.hasReceivedLink ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("hasReceivedLink", !filters.hasReceivedLink)}
                            >
                                <TbLink />{t("filters.hasReceivedLink")}
                            </button>
                        </div>
                    </div>

                    <div className="inv-filter__row inv-filter__row--wrap">
                        <span className="inv-filter__label">{t("filters.portal")}</span>
                        <div className="inv-filter__chips">
                            <button
                                className={`inv-filter__chip inv-filter__chip--green${filters.questionnaireSent ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("questionnaireSent", !filters.questionnaireSent)}
                            >
                                <TbClipboardCheck />{t("filters.questionnaireSubmitted")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--neutral${filters.questionnaireNotSubmitted ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("questionnaireNotSubmitted", !filters.questionnaireNotSubmitted)}
                            >
                                <TbClipboardX />{t("filters.questionnaireNotSubmitted")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--neutral${filters.noQuestionnaireSent ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("noQuestionnaireSent", !filters.noQuestionnaireSent)}
                            >
                                <TbClipboardX />{t("filters.noQuestionnaireSent")}
                            </button>
                        </div>
                    </div>

                    <div className="inv-filter__row inv-filter__row--wrap">
                        <span className="inv-filter__label">{t("filters.missingInfo")}</span>
                        <div className="inv-filter__chips">
                            <button
                                className={`inv-filter__chip inv-filter__chip--red${filters.noEmail ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("noEmail", !filters.noEmail)}
                            >
                                <TbMailOff />{t("filters.noEmail")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--red${filters.noTel ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("noTel", !filters.noTel)}
                            >
                                <TbPhoneOff />{t("filters.noTel")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--red${filters.noAddress ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("noAddress", !filters.noAddress)}
                            >
                                <TbMapPinOff />{t("filters.noAddress")}
                            </button>
                        </div>
                    </div>

                    {!!groups?.length && (
                        <div className="inv-filter__row inv-filter__row--wrap">
                            <span className="inv-filter__label">{t("groups.group")}</span>
                            <div className="inv-filter__chips">
                                {groups.map((group) => (
                                    <button
                                        key={group.id}
                                        className={`inv-filter__chip inv-filter__chip--tag${filters.group === group.id ? " inv-filter__chip--active" : ""}`}
                                        style={getTagStyle(group.name) as CSSProperties}
                                        onClick={() => set("group", filters.group === group.id ? "" : group.id)}
                                    >
                                        {group.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("form.patient")}</span>
                        <input
                            type="text"
                            className="inv-filter__input"
                            aria-label={t("form.patient")}
                            placeholder={t("filters.search")}
                            value={filters.patient}
                            onChange={(e) => set("patient", sanitizeInput(e.target.value))}
                        />
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("form.tel")}</span>
                        <input
                            type="text"
                            className="inv-filter__input"
                            aria-label={t("form.tel")}
                            placeholder={t("filters.search")}
                            value={filters.tel}
                            onChange={(e) => set("tel", sanitizeInput(e.target.value))}
                        />
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("form.address")}</span>
                        <input
                            type="text"
                            className="inv-filter__input"
                            aria-label={t("form.address")}
                            placeholder={t("filters.addressPlaceholder")}
                            value={filters.address}
                            onChange={(e) => set("address", sanitizeInput(e.target.value))}
                        />
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("form.email")}</span>
                        <input
                            type="text"
                            className="inv-filter__input"
                            aria-label={t("form.email")}
                            placeholder={t("filters.search")}
                            value={filters.email}
                            onChange={(e) => set("email", sanitizeInput(e.target.value))}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
